import * as THREE from "three";

/** Texture resolution for one board cell. Text is rasterised at this scale. */
export const CELL_PX = 128;

const TEXT_COLOR = "#ffd700";
const OUTLINE_COLOR = "#000000";
/** Same proportion as the original: board width / 120 for a board of eight cells. */
const OUTLINE_PX = (CELL_PX * 8) / 120;
const FONT_FAMILY = "Cantarell, 'Segoe UI', system-ui, sans-serif";

/** Font size of the floating score numbers, in pixels at CELL_PX per cell. */
export const SCORE_FONT_PX = 65;
/** Font size of the full-board messages, in pixels at CELL_PX per cell. */
export const MESSAGE_FONT_PX = 82;

/**
 * Renders gold, black-outlined bold text onto a transparent canvas texture, like the original's
 * generic_text_canvas_draw. The canvas covers `widthCells` by `heightCells` board cells.
 */
export function makeTextTexture(
  text: string,
  widthCells: number,
  heightCells: number,
  fontPx: number,
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = widthCells * CELL_PX;
  canvas.height = heightCells * CELL_PX;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas is not available");

  ctx.font = `700 ${fontPx}px ${FONT_FAMILY}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.lineWidth = OUTLINE_PX;
  ctx.strokeStyle = OUTLINE_COLOR;
  ctx.fillStyle = TEXT_COLOR;

  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  ctx.strokeText(text, cx, cy);
  ctx.fillText(text, cx, cy);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Pixels per board cell in the background texture. */
const BACKGROUND_CELL_PX = 64;

/** Light and dark squares of the board background (board_bg.svg), `width` by `height` cells. */
export function makeBoardTexture(width: number, height: number): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = width * BACKGROUND_CELL_PX;
  canvas.height = height * BACKGROUND_CELL_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas is not available");

  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? "#211010" : "#4a3839";
      ctx.fillRect(
        x * BACKGROUND_CELL_PX,
        y * BACKGROUND_CELL_PX,
        BACKGROUND_CELL_PX,
        BACKGROUND_CELL_PX,
      );
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  return texture;
}

/** A rounded outline used for keyboard focus, drawn in white so it differs from the cursor. */
export function makeFocusTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = CELL_PX;
  canvas.height = CELL_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas is not available");

  const inset = 6;
  const radius = 16;
  ctx.lineWidth = 8;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
  ctx.beginPath();
  ctx.roundRect(inset, inset, CELL_PX - inset * 2, CELL_PX - inset * 2, radius);
  ctx.stroke();

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
